<template>
  <div>
    <div v-if="!accountStatuses.length" class="h-8 skeleton rounded-md" style="width: 235px" />
    <button v-else class="data-health-btn flex items-center gap-1.5 pl-1.5 pr-2.5 py-1.5 rounded-md fade-in"
    :class="{'problem': showProblem}" @click="showHealthModal = true">
      <InfoIcon v-if="showProblem" class="text-secondary-red-300" />
      <RefreshIcon v-else class="refresh-icon text-icon-normal" :class="{'pulling': lensIsRefreshing}" />
      <BaseText type="label" size="sm" class="capitalize" :class="showProblem ? 'text-secondary-red-300' : 'text-text-muted'">
        {{ lensIsRefreshing ? 'Refreshing...' : healthStatus }}
      </BaseText>
      <BaseText type="body" size="sm" :class="showProblem ? 'text-secondary-red-300' : 'text-text-normal'">
        Last updated {{ getTimeFromNow(lastRefreshedAt) }} ago
      </BaseText>
    </button>
    <DataHealthModal
      v-if="showHealthModal && accountStatuses.length"
      :account-statuses="accountStatuses"
      :time-range="timeRange"
      :last-refreshed-at="lastRefreshedAt"
      :health-status="healthStatus"
      :lens-is-refreshing.sync="lensIsRefreshing"
      @refreshLensData="refreshLensData"
      @close="showHealthModal = false"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import LensAPI from '@/api/lens'
import moment from 'moment'

import DataHealthModal from './DataHealthModal.vue'

import RefreshIcon from '../globals/Icons/LensIcons/RefreshIcon.vue'
import InfoIcon from '../globals/Icons/InfoSimpleIcon.vue'

export default {
  name: 'DataHealthButton',
  components: {
    DataHealthModal,
    RefreshIcon,
    InfoIcon
  },
  props: {
    lastRefreshedAt: {
      type: String,
      required: true
    },
    dateRange: {
      type: Object,
      default: () => ({
        start: null,
        end: null
      })
    }
  },
  data () {
    return {
      healthStatus: 'N/A',
      accountStatuses: [],
      lensIsRefreshing: false,

      showHealthModal: false,
      statusPollInterval: null
    }
  },
  async mounted () {
    this.fetchDataAccuracy()
  },
  beforeDestroy () {
    clearInterval(this.statusPollInterval)
  },
  watch: {
    dateRange () {
      this.fetchDataAccuracy()
    },
    lensIsRefreshing (oldVal, newVal) {
      if (newVal) {
        this.startStatusPoll()
      } else {
        if (oldVal) this.$emit('refreshComplete')
        clearInterval(this.statusPollInterval)
      }
    },
    lens () {
      this.healthStatus = this.getHealthStatus(this.accountStatuses)
    }
  },
  computed: {
    ...mapGetters('LensModule', ['getSidebarLenses']),
    lens () {
      return this.getSidebarLenses.find(lens => lens.id === this.$route.params.lensId)
    },
    showProblem () {
      return this.healthStatus === 'connection error' || (!this.lensIsRefreshing && this.healthStatus === 'problem')
    },
    timeRange () {
      const { start, end } = this.dateRange
      if (!start || !end) return null
      return {
        start: moment(start).format('YYYY-MM-DD'),
        end: moment(end).format('YYYY-MM-DD')
      }
    }
  },
  methods: {
    async fetchDataAccuracy (isPolling = false) {
      if (!this.timeRange) return
      
      const { data: statusData } = await LensAPI.Lens.getLensStatusV2(this.$route.params.lensId, this.timeRange)
      const { ad_accounts: accountStatuses } = statusData

      this.accountStatuses = accountStatuses
      this.healthStatus = this.getHealthStatus(accountStatuses)
      this.lensIsRefreshing = accountStatuses.some(account => account.recently_refreshed)

      if (this.lensIsRefreshing && !isPolling) this.startStatusPoll()
    },
    async refreshLensData () {
      this.lensIsRefreshing = true
      try {
        await LensAPI.Lens.refreshLensData(this.$route.params.lensId)
      } catch (error) {
        console.error('Error refreshing lens data:', error)
        // Handle specific case where data is already being refreshed
        if (error.response && error.response.status === 422) {
          this.$showAlert({ type: 'info', message: 'Data is currently being refreshed.' })
        } else {
          this.$showAlert({ type: 'error', message: 'An error occurred while refreshing lens data.' })
          this.lensIsRefreshing = false
        }
      }
    },
    startStatusPoll () {
      this.statusPollInterval = setInterval(() => {
        const isPolling = true
        this.fetchDataAccuracy(isPolling)
      }, 5 * 60 * 1000) // 5 minutes
    },
    getTimeFromNow (timestamp) {
      if (!timestamp) return 'N/A'

      const now = new Date()
      const updated = new Date(timestamp)
      const diffMs = now - updated

      // Convert to appropriate time unit
      const diffSec = Math.floor(diffMs / 1000)
      const diffMin = Math.floor(diffSec / 60)
      const diffHour = Math.floor(diffMin / 60)
      const diffDay = Math.floor(diffHour / 24)
      const diffMonth = Math.floor(diffDay / 30)
      const diffYear = Math.floor(diffMonth / 12)

      if (diffYear > 0) return `${diffYear}y`
      if (diffMonth > 0) return `${diffMonth}mo`
      if (diffDay > 0) return `${diffDay}d`
      if (diffHour > 0) return `${diffHour}h`
      if (diffMin > 0) return `${diffMin}m`
      return `${diffSec}s`
    },
    getHealthStatus (accountStatuses) {
      if (!this.lens) return ''
      if (this.lens.isDisconnected) return 'connection error'
      
      const avgPercentDiff = accountStatuses.reduce((acc, account) => acc + account.data_accuracy.percent_diff, 0) / accountStatuses.length
      if (avgPercentDiff < 4) return 'healthy'
      if (avgPercentDiff < 10) return 'ok'
      return 'problem'
    }
  }
}
</script>

<style scoped>
.data-health-btn {
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 100ms ease-in-out;
}
.data-health-btn:hover {
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.17), 0 0 0 1px rgba(0, 56, 108, 0.12);
}
.data-health-btn.problem {
  box-shadow: none;
  background-color: #FFEFF3;
  transition: background-color 100ms ease-in-out;
}
.data-health-btn.problem:hover {
  background-color: #FCD8DF;
}

.refresh-icon {
  transform: scaleX(-1);
}
.refresh-icon.pulling {
  animation: refreshIconPulling 3s linear infinite;
}
@keyframes refreshIconPulling {
  from {
    transform: rotate(0deg) scaleX(-1);
  } 
  to {
    transform: rotate(360deg) scaleX(-1);
  }
}
.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>